/**
 * useProviderSchedule - Conflitos de horário do prestador (Fase 7 OS)
 *
 * BR-OS-004 / BR-OS-008: consulta, antes de salvar, se o prestador já tem
 * compromisso no período. O servidor bloqueia de novo ao gravar (ERRCODE OS409).
 *
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 */
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ScheduleConflict { kind: 'service_order' | 'event'; id: string; title: string; start_at: string; end_at: string }
export interface ScheduleCheck { conflicts: ScheduleConflict[]; scheduled_end: string; suggested_start: string | null }

/** Código de erro do gatilho de conflito no banco. */
export const SCHEDULE_CONFLICT_CODE = 'OS409';

/** Converte o valor do campo datetime-local para ISO (fuso do navegador). */
export const localDateTimeToIso = (v: string) => (v ? new Date(v).toISOString() : null);

/** Converte ISO para o formato do campo datetime-local. */
export const isoToLocalDateTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function useProviderScheduleCheck() {
  const { currentTenant } = useAuth();
  return useCallback(async (args: { providerId: string; start: string; serviceTypeId: string; excludeId?: string | null }) => {
    const { data, error } = await (supabase.rpc as any).call(supabase, 'get_provider_schedule_conflicts', {
      p_provider_person_id: args.providerId,
      p_scheduled_start: localDateTimeToIso(args.start),
      p_service_type_id: args.serviceTypeId,
      p_exclude_id: args.excludeId ?? null,
      p_tenant_hint: currentTenant?.id ?? null,
    });
    if (error) throw error;
    return data as ScheduleCheck;
  }, [currentTenant?.id]);
}
